<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

// Pull-to-refresh «как в нативных приложениях»: тянем список вниз от самого
// верха, появляется лоадер, отпускаем за порогом — выполняется refresh.
const props = withDefaults(defineProps<{
  threshold?: number;   // дистанция (px), после которой отпускание триггерит refresh
  max?: number;         // максимум, докуда можно оттянуть
  disabled?: boolean;
}>(), {
  threshold: 70,
  max: 110,
  disabled: false,
});

const emit = defineEmits<{ refresh: [] }>();

const scroller = ref<HTMLElement | null>(null);
const pull = ref(0);          // текущая дистанция оттягивания (px)
const dragging = ref(false);  // палец на экране и идёт оттягивание
const refreshing = ref(false);

let startY = 0;
let active = false;           // жест начат у самого верха — это pull, а не скролл

// Резиновое сопротивление: чем дальше тянем, тем медленнее идёт.
function resist(dy: number) {
  return Math.min(props.max, dy * 0.5);
}

const progress = computed(() => Math.min(1, pull.value / props.threshold));
const ready = computed(() => pull.value >= props.threshold);

function onTouchStart(e: TouchEvent) {
  const touch = e.touches[0];
  if (props.disabled || refreshing.value || e.touches.length !== 1 || !touch) {
    active = false;
    return;
  }
  active = (scroller.value?.scrollTop ?? 0) <= 0;
  startY = touch.clientY;
}

function onTouchMove(e: TouchEvent) {
  const touch = e.touches[0];
  if (!active || refreshing.value || !touch) return;
  const dy = touch.clientY - startY;
  if (dy <= 0) {
    // Палец пошёл вверх — это обычный скролл, отдаём управление списку.
    pull.value = 0;
    dragging.value = false;
    active = false;
    return;
  }
  dragging.value = true;
  pull.value = resist(dy);
  // Блокируем нативный overscroll/скролл, пока тянем индикатор.
  if (e.cancelable) e.preventDefault();
}

function onTouchEnd() {
  if (!active) return;
  active = false;
  dragging.value = false;
  if (ready.value && !refreshing.value) {
    refreshing.value = true;
    pull.value = props.threshold;   // подвешиваем индикатор на позиции лоадера
    emit('refresh');
  } else {
    pull.value = 0;
  }
}

// Слушатели вешаем вручную: touchmove должен быть { passive: false },
// иначе preventDefault() не сработает и нативный overscroll нас перебьёт.
onMounted(() => {
  const el = scroller.value;
  if (!el) return;
  el.addEventListener('touchstart', onTouchStart, { passive: true });
  el.addEventListener('touchmove', onTouchMove, { passive: false });
  el.addEventListener('touchend', onTouchEnd, { passive: true });
  el.addEventListener('touchcancel', onTouchEnd, { passive: true });
});

onBeforeUnmount(() => {
  const el = scroller.value;
  if (!el) return;
  el.removeEventListener('touchstart', onTouchStart);
  el.removeEventListener('touchmove', onTouchMove);
  el.removeEventListener('touchend', onTouchEnd);
  el.removeEventListener('touchcancel', onTouchEnd);
});
</script>

<template>
  <div class="relative h-full overflow-hidden">
    <!-- Индикатор: спрятан над верхом, выезжает вместе с оттягиванием -->
    <div
      class="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center"
      :style="{
        transform: `translateY(${pull - 40}px)`,
        transition: dragging ? 'none' : 'transform 0.25s ease',
      }"
    >
      <div class="mt-2 flex h-8 w-8 items-center justify-center rounded-full bg-bg-card shadow-lg ring-1 ring-stone-800">
        <svg
          viewBox="0 0 24 24"
          class="h-5 w-5 text-amber-400"
          :class="{ 'animate-spin': refreshing }"
          :style="refreshing ? {} : { opacity: progress, transform: `rotate(${pull * 2.5}deg)` }"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <template v-if="refreshing">
            <path d="M12 3a9 9 0 1 0 9 9" />
          </template>
          <template v-else>
            <path d="M12 5v14" />
            <path :d="ready ? 'M6 11l6 6 6-6' : 'M6 13l6-6 6 6'" />
          </template>
        </svg>
      </div>
    </div>

    <div
      ref="scroller"
      class="h-full overflow-y-auto overscroll-y-contain"
      :style="{
        transform: `translateY(${pull}px)`,
        transition: dragging ? 'none' : 'transform 0.25s ease',
      }"
    >
      <slot />
    </div>
  </div>
</template>
